import {
  useSearchTaskTagsLazyQuery,
  QuestionTagCategory,
  OrderType,
} from '@exam-management/graphql';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import {
  FormSearchMultipleSelection,
  type FormSearchMultipleSelectionProps,
  type OptionSelectionProps,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';

export interface CognitiveDomainFieldProps<TFieldValues extends FieldValues>
  extends Omit<FormSearchMultipleSelectionProps<TFieldValues>, 'options' | 'label'> {
  helperText?: React.ReactNode;
}

export default function CognitiveDomainField<TFieldValues extends FieldValues>(
  props: Readonly<CognitiveDomainFieldProps<TFieldValues>>,
) {
  const { name, helperText, placeholder, ...restProps } = props;
  const [isOpen, { open, close }] = useDisclosure();
  const [cognitiveDomainOptions, setCognitiveDomainOptions] = useState<OptionSelectionProps[]>([]);
  const { task } = useQuestionCreationTaskDetails();

  const [getCognitiveDomainTags, { loading: isSearchTagsLoading }] = useSearchTaskTagsLazyQuery({
    onCompleted: (data) => {
      const tagsResult = data.qbsSearchTaskTags.tags.map((tag) => {
        return { label: tag.name, value: tag.id };
      });
      setCognitiveDomainOptions(tagsResult);
    },
  });

  const handleOpenOptions = () => {
    getCognitiveDomainTags({
      variables: {
        search: {
          category: QuestionTagCategory.CognitiveDomain,
        },
        paging: {
          pageSize: 100,
          currentPage: 0,
        },
        order: {
          field: 'name',
          type: OrderType.Asc,
        },
        taskId: task.id,
      },
    });
    open();
  };

  const noOptionsText = helperText ?? 'ไม่พบข้อมูล';

  return (
    <FormSearchMultipleSelection
      label='คุณลักษณะของข้อสอบ (Cognitive Domain)'
      options={cognitiveDomainOptions}
      name={name}
      placeholder={placeholder}
      loading={isSearchTagsLoading}
      noOptionsText={noOptionsText}
      open={isOpen}
      onOpen={handleOpenOptions}
      onClose={close}
      {...restProps}
    />
  );
}
